import { IconFilter, IconPlus, IconRefresh, IconX } from "@tabler/icons-react";
import { getRouteApi, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { BotonExportarExcel, Button, Card, Group, IconAction, PageHeader, PaginadorTabla, Stack, Switch, Text } from "@/components/ui";
import { DesktopOnly, MobileOnly } from "@/components/ui/ResponsiveTableList";
import { useMisSolicitudes } from "@/features/solicitudes/hooks/use-mis-solicitudes";
import { solicitudesApi } from "@/features/solicitudes/services/solicitudes.api";
import {
  filtrarSolicitudes,
  filtrosNoVacios,
  type SolicitudFiltros,
  type SolicitudSort,
} from "@/features/solicitudes/utils/ordenar-solicitudes";
import { PAGE_SIZE_SOLICITUDES, paramsMisSolicitudes } from "@/features/solicitudes/utils/params-mis-solicitudes";
import { FiltrosActivos } from "./FiltrosActivos";
import { useAuthStore, useUserPermisos } from "@/lib/auth-store";
import { PERMISOS } from "@/lib/permisos";
import { etiquetaEstado } from "@/lib/estado-labels";
import { useDebouncedValue } from "@/lib/hooks/use-debounced-value";
import type { MiSolicitud } from "@/lib/types";
import type { ColumnaExcel } from "@/shared/export/exportar-excel";
import { EmptyState } from "./SolicitudesEmptyStates";
import { FiltrosFacturaActivos } from "./FiltrosFacturaActivos";
import { filtrosFacturaNoVacios, hayFiltrosFactura, type FiltrosFactura } from "../utils/filtros-factura";
import { SolicitudCardMobile } from "./SolicitudCardMobile";
import { SolicitudesStats, type QuickFilter } from "./SolicitudesStats";
import { SolicitudesTable } from "./SolicitudesTable";
import { MisSolicitudesSkeleton } from "./MisSolicitudesSkeleton";
import { encabezadoSolicitudes, esVistaSoloPropias } from "@/features/solicitudes/utils/encabezado-solicitudes";

/** Etiqueta legible de la categoría de resultado (deep-link del donut del dashboard). */
const CATEGORIA_LABEL: Record<string, string> = {
  EnProceso: "En proceso",
  EnAutorizacion: "En autorización",
  Autorizada: "Autorizadas",
  Cancelada: "Canceladas por mí",
  RechazadaPorReglas: "Rechazadas por reglas",
  RechazadaPorSat: "Rechazadas por SAT",
  BajaMesaControl: "Baja (Mesa de Control)",
  DescartadaPlazoVencido: "Descartadas (plazo vencido)",
};

const routeApi = getRouteApi("/_authed-tw/solicitudes/");

/** Preferencia por navegador del switch «Mostrar facturas». */
const LLAVE_MOSTRAR_FACTURAS = "mis-solicitudes:mostrar-facturas";

/**
 * Container del listado "Mis solicitudes" · orquesta query con polling, stats y
 * el filtrado por columna (fila de búsqueda bajo los encabezados de la tabla):
 * filtros, orden y PAGINADO se resuelven en el SERVIDOR (query params, con
 * debounce); el filtro también se aplica en cliente sobre la página visible
 * para feedback instantáneo · igual que la Bandeja de autorización.
 */
export function MisSolicitudesPage() {
  const navigate = useNavigate();
  const roles = useAuthStore((s) => s.user?.roles) ?? [];
  // Espejo exacto de `soloPropias` en `ListarMisSolicitudesHandler` (backend) · decide
  // si el viewer ve TODAS las solicitudes (con nombre de solicitante) o solo las suyas.
  const soloPropias = esVistaSoloPropias(roles);
  const encabezado = encabezadoSolicitudes(soloPropias);

  const [filtros, setFiltros] = useState<SolicitudFiltros>({});
  const [sort, setSort] = useState<SolicitudSort | null>(null);
  const [page, setPage] = useState(1);
  // Fila de filtros por columna COLAPSADA por default (revisión UX · 11 inputs
  // simultáneos eran ruido visual). Los filtros aplicados siguen visibles como
  // chips aunque la fila esté cerrada.
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  // Quick filter de los cards de stats (toggle · click en el card activo o en
  // "Total" lo limpia) · filtra en el SERVIDOR igual que los filtros de
  // columna. Las stats de los cards vienen pre-quickFilter del server, así los
  // conteos no "desaparecen" al activar un card. Se INICIALIZA desde el search
  // param `?filtro=` (deep-link desde los KPIs del dashboard); los toggles
  // posteriores son locales.
  const { filtro: filtroInicial, categoria: categoriaInicial, incluir } = routeApi.useSearch();
  const [quickFilter, setQuickFilter] = useState<QuickFilter>(filtroInicial ?? null);
  // Categoría de resultado (deep-link del donut del dashboard) · mutuamente
  // excluyente con el quick filter de los cards: interactuar con un card la limpia.
  const [categoria, setCategoria] = useState<string | undefined>(categoriaInicial);
  // Toggle "Mostrar canceladas/dadas de baja" · oculto por default (ruido para el
  // seguimiento diario). Las categorías terminales del donut (Canceladas/Baja) lo
  // pre-activan por deep-link (`?incluir=true`) para no ocultar lo que se pidió ver.
  const [incluirCanceladasYBajas, setIncluirCanceladasYBajas] = useState(incluir ?? false);
  // Switch «Mostrar facturas» · expande/contrae a la vez las filas con facturas o invoice.
  // Se recuerda por navegador (preferencia del usuario · si el storage falla, arranca apagado).
  const [mostrarFacturas, setMostrarFacturasState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(LLAVE_MOSTRAR_FACTURAS) === "1";
    } catch {
      return false;
    }
  });
  const setMostrarFacturas = (v: boolean) => {
    setMostrarFacturasState(v);
    try {
      localStorage.setItem(LLAVE_MOSTRAR_FACTURAS, v ? "1" : "0");
    } catch {
      /* storage no disponible · solo afecta el recuerdo de la preferencia */
    }
  };
  const [filtrosDebounced] = useDebouncedValue(filtros, 350);
  // Fila de filtros por FACTURA (UUID, serie-folio, emisor, fecha de timbrado, total) · filtra
  // en el servidor como los demás filtros.
  const [filtrosFactura, setFiltrosFactura] = useState<FiltrosFactura>({});
  const [filtrosFacturaDebounced] = useDebouncedValue(filtrosFactura, 350);
  const facturaActiva = hayFiltrosFactura(filtrosFacturaDebounced);

  // Misma construcción que el loader de la ruta (`paramsInicialesMisSolicitudes`) ·
  // así la primera página sale de la precarga y no del skeleton.
  const serverParams = useMemo(
    () => paramsMisSolicitudes({ filtros: filtrosDebounced, sort, quickFilter, categoria, incluirCanceladasYBajas, page, filtrosFactura: filtrosFacturaDebounced }),
    [filtrosDebounced, sort, quickFilter, categoria, incluirCanceladasYBajas, page, filtrosFacturaDebounced],
  );

  // Cambiar los filtros (o el toggle, o el quick filter, o la categoría) invalida
  // la página actual · siempre de vuelta a la 1.
  useEffect(() => {
    setPage(1);
  }, [filtrosDebounced, incluirCanceladasYBajas, quickFilter, categoria, filtrosFacturaDebounced]);

  const { data, isPending, isError, error, refetch, isFetching, isPlaceholderData } = useMisSolicitudes(serverParams);

  // El botón "refrescar" hace UN giro completo cada vez que arranca una recarga
  // (switch, filtros o click) · relanzar la animación cambiando la `key` evita
  // el parpadeo de un spin que arranca/para de golpe en fetches instantáneos.
  const [giroKey, setGiroKey] = useState(0);
  const primerRender = useRef(true);
  useEffect(() => {
    if (primerRender.current) {
      primerRender.current = false;
      return;
    }
    if (isFetching) setGiroKey((k) => k + 1);
  }, [isFetching]);

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const stats = data?.stats;
  const hayFiltros = Object.keys(filtrosNoVacios(filtrosDebounced)).length > 0 || facturaActiva;
  const nFiltros =
    Object.keys(filtrosNoVacios(filtrosDebounced)).length + Object.keys(filtrosFacturaNoVacios(filtrosFacturaDebounced)).length;

  // El móvil no tiene fila de filtros · aplicamos el mismo filtro por columna en cliente.
  const itemsMovil = useMemo(() => filtrarSolicitudes(items, filtros), [items, filtros]);

  const irANueva = () => navigate({ to: "/solicitudes/nueva" });
  // Hotspot 96 · crear solicitudes exige `solicitudes.gestionar` (la ruta destino y el endpoint);
  // quien solo consulta (Visor, Auditor, Administrador, Mesa de Control) no ve los botones.
  const puedeCrear = useUserPermisos().includes(PERMISOS.solicitudesGestionar);

  // Export a Excel · trae TODAS las páginas con los filtros/orden actuales.
  const obtenerTodas = async (): Promise<MiSolicitud[]> => {
    const params = paramsMisSolicitudes({ filtros: filtrosDebounced, sort, quickFilter, categoria, incluirCanceladasYBajas, page: 1, filtrosFactura: filtrosFacturaDebounced });
    const todas: MiSolicitud[] = [];
    for (let p = 1; ; p++) {
      const pagina = await solicitudesApi.listarMias({ ...params, page: p, pageSize: 100 });
      todas.push(...pagina.items);
      if (todas.length >= pagina.total || pagina.items.length === 0) break;
    }
    return todas;
  };

  const columnasExcel: ColumnaExcel<MiSolicitud>[] = [
    { header: "Folio", value: (s) => s.folio },
    ...(!soloPropias ? [{ header: "Solicitante", value: (s: MiSolicitud) => s.solicitanteNombre } satisfies ColumnaExcel<MiSolicitud>] : []),
    { header: "Estado", value: (s) => etiquetaEstado(s.estado, { nivelActual: s.flujoNivelActual, rolActual: s.flujoRolActual }) },
    { header: "Tipo", value: (s) => s.tipo },
    { header: "Subtipo", value: (s) => s.subtipo },
    { header: "CA/OC", value: (s) => s.claveCaOc ?? null },
    { header: "Organización", value: (s) => s.organizacionNombre },
    { header: "Área", value: (s) => s.areaNombre },
    { header: "Proveedor", value: (s) => s.proveedorRazonSocial },
    { header: "Núm. proveedor", value: (s) => s.proveedorNumero },
    { header: "RFC proveedor", value: (s) => s.proveedorRfc },
    { header: "Concepto", value: (s) => s.descripcionPrimerConcepto },
    { header: "Total CFDI", value: (s) => s.totalCfdi },
    { header: "Moneda", value: (s) => s.monedaCfdi },
    { header: "RFC emisor", value: (s) => s.rfcEmisor },
    { header: "Documentos", value: (s) => s.cantidadDocumentos },
    { header: "Contabilizada", value: (s) => (s.enviadaAContabilizacion ? "Sí" : "No") },
    { header: "Fecha de pago", value: (s) => s.fechaPago },
    { header: "Último evento", value: (s) => s.fechaUltimoEvento },
  ];

  // Primera carga (sin datos · `keepPreviousData` evita volver aquí al filtrar o
  // paginar) · MISMO skeleton que el `pendingComponent` de la ruta.
  if (isPending) return <MisSolicitudesSkeleton />;

  return (
    <div className="w-full px-4 py-6 md:px-6">
      <PageHeader
        title={encabezado.titulo}
        description={encabezado.descripcion}
        actions={
          <>
            <Switch
              label="Mostrar facturas"
              checked={mostrarFacturas}
              onCheckedChange={setMostrarFacturas}
            />
            <Switch
              label="Mostrar canceladas/dadas de baja"
              checked={incluirCanceladasYBajas}
              onCheckedChange={setIncluirCanceladasYBajas}
            />
            <BotonExportarExcel archivo="solicitudes-de-pago" hoja="Solicitudes" columnas={columnasExcel} obtenerFilas={obtenerTodas} />
            {/* Animación suave: al pasar el mouse el ícono gira 90° (invita a
                recargar); en cada recarga da UNA vuelta completa de 0.6s
                (`animate-refrescar-giro`, relanzada por `giroKey`). Sin
                `disabled` para no parpadear la opacidad en fetches instantáneos. */}
            <IconAction
              label="Refrescar"
              variant="default"
              size="lg"
              onClick={() => refetch()}
              className="[&_svg]:transition-transform [&_svg]:duration-500 [&_svg]:ease-out-quint hover:[&_svg]:rotate-90"
            >
              <IconRefresh
                key={giroKey}
                size={16}
                className={giroKey > 0 ? "motion-safe:animate-refrescar-giro" : undefined}
              />
            </IconAction>
            {puedeCrear ? (
              <Button leftSection={<IconPlus size={16} />} onClick={irANueva}>
                Nueva solicitud
              </Button>
            ) : null}
          </>
        }
      />
      <Stack gap="md">
        {/* Los conteos vienen pre-quickFilter del server (stats.total incluido) ·
            `total` (paginación) sí refleja el quick filter, por eso el card
            "Total" usa stats.total. Click en el card activo (o en "Total") limpia. */}
        <SolicitudesStats
          total={stats?.total ?? total}
          enCaptura={stats?.enCaptura ?? 0}
          requierenAccion={stats?.requierenAccion ?? 0}
          enAutorizacion={stats?.enAutorizacion ?? 0}
          // Sin stats (error de la primera carga) y reintentando · barras, no un «0» falso.
          cargando={!stats && isFetching}
          quickFilter={quickFilter}
          onQuickFilterChange={(v) => {
            setCategoria(undefined); // los cards y la categoría del donut son excluyentes
            setQuickFilter((prev) => (prev === v ? null : v));
          }}
        />

        {isError ? (
          <Card>
            <Text c="peligro-600" size="sm">
              Error: {(error as Error).message}
            </Text>
          </Card>
        ) : total === 0 && !hayFiltros && !quickFilter && !categoria ? (
          <EmptyState onCrear={puedeCrear ? irANueva : undefined} />
        ) : (
          <>
            {/* Toolbar de la tabla · toggle de la fila de filtros (con contador
                de activos) + chips de lo aplicado a la izquierda, paginación a
                la derecha. El botón solo aplica en desktop (la fila de filtros
                vive en la tabla desktop); los chips sí operan también sobre el
                filtrado en cliente del listado móvil. */}
            {/* Toolbar (filtros + paginación) STICKY bajo el header fijo · fondo
                opaco + bleed lateral para cubrir el contenido que pasa debajo. */}
            <div className="bg-pagina sticky top-[var(--shell-header-h)] z-20 -mx-4 px-4 py-2 md:-mx-6 md:px-6">
            <Group justify="space-between" align="flex-start" gap="sm" wrap="wrap">
              <Group gap="sm" align="center" wrap="wrap">
                <DesktopOnly>
                  <Button
                    size="sm"
                    variant={mostrarFiltros ? "light" : "default"}
                    leftSection={<IconFilter size={14} stroke={1.8} />}
                    onClick={() => setMostrarFiltros((v) => !v)}
                    aria-pressed={mostrarFiltros}
                    aria-label={mostrarFiltros ? "Ocultar fila de filtros" : "Mostrar fila de filtros"}
                  >
                    Filtros{nFiltros > 0 ? ` · ${nFiltros}` : ""}
                  </Button>
                </DesktopOnly>
                {categoria ? (
                  <button
                    type="button"
                    onClick={() => setCategoria(undefined)}
                    aria-label={`Quitar filtro Categoría: ${CATEGORIA_LABEL[categoria] ?? categoria}`}
                    className="group inline-flex max-w-60 items-center gap-1 rounded-full border border-input bg-muted/40 py-0.5 pl-2.5 pr-1.5 text-xs text-foreground transition-colors hover:border-peligro-300 hover:bg-peligro-50 dark:hover:bg-peligro-900/20"
                  >
                    <span className="text-muted-foreground">Categoría:</span>
                    <span className="truncate font-medium">{CATEGORIA_LABEL[categoria] ?? categoria}</span>
                    <IconX size={12} stroke={2} className="shrink-0 text-muted-foreground group-hover:text-peligro-600" />
                  </button>
                ) : null}
                <FiltrosActivos
                  filtros={filtros}
                  onQuitar={(key) => setFiltros((prev) => ({ ...prev, [key]: "" }))}
                  onLimpiar={() => setFiltros({})}
                />
                <FiltrosFacturaActivos
                  filtros={filtrosFactura}
                  onQuitar={(key) => setFiltrosFactura((prev) => ({ ...prev, [key]: "" }))}
                  onLimpiar={() => setFiltrosFactura({})}
                />
              </Group>
              <PaginadorTabla total={total} page={page} pageSize={PAGE_SIZE_SOLICITUDES} onPageChange={setPage} variante="inferior" sustantivo="solicitudes" />
            </Group>
            </div>
            <SolicitudesTable
              solicitudes={items}
              filtros={filtros}
              sort={sort}
              buscando={isPlaceholderData}
              mostrarFiltros={mostrarFiltros}
              // Con la búsqueda por factura activa se expanden las filas encontradas para ver
              // directamente la factura que coincidió.
              expandirTodas={mostrarFacturas || facturaActiva}
              filtrosFactura={filtrosFactura}
              onFiltrarFactura={(key, valor) => setFiltrosFactura((prev) => ({ ...prev, [key]: valor }))}
              onFiltrar={(key, valor) => setFiltros((prev) => ({ ...prev, [key]: valor }))}
              onOrdenar={setSort}
            />
            <MobileOnly className="flex flex-col gap-2">
              {itemsMovil.map((s) => (
                <SolicitudCardMobile key={`m-${s.id}`} s={s} expandirTodas={mostrarFacturas || facturaActiva} />
              ))}
            </MobileOnly>
          </>
        )}
      </Stack>
    </div>
  );
}
